/**
 * WakeWordService.ts
 *
 * Continuous mic capture for wake word detection via the backend Wyoming
 * OpenWakeWord service. Streams PCM16 mono 16 kHz audio chunks over
 * Socket.IO (`agent:wakeword:audio`). The backend handles detection and
 * notifies back via `agent:wakeword:detected`.
 *
 * Audio pipeline:
 *   getUserMedia → AudioContext (native rate) → ScriptProcessorNode
 *   → resample to 16 kHz → Float32→Int16 → socket.emit('agent:wakeword:audio')
 */

import { EventEmitter } from 'eventemitter3';
import { errorMessage, errorName } from '../utils/errors';

const TARGET_SAMPLE_RATE = 16_000;
const CHUNK_SIZE = 4_096; // samples per ScriptProcessor buffer
const GATE_OPEN_RMS = 0.018;
const GATE_CLOSE_RMS = 0.012;
const GATE_HANGOVER_MS = 700;
const SILENT_KEEPALIVE_MS = 750;
const PRE_ROLL_CHUNKS = 3;

/**
 * AudioWorklet processor: forwards each render-quantum input buffer to the
 * main thread, where it is accumulated, resampled to 16 kHz and streamed to
 * the backend (same pipeline as the old ScriptProcessor path).
 */
const WAKE_WORD_WORKLET_SOURCE = `
class WakeWordCaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const input = inputs[0];
    if (input && input.length > 0) {
      const channel = input[0];
      if (channel && channel.length > 0) {
        this.port.postMessage(new Float32Array(channel));
      }
    }
    return true;
  }
}
registerProcessor('otto-wake-word-capture', WakeWordCaptureProcessor);
`;

interface WakeWordEvents {
  'level-change': (level: number) => void;
  'no-audio': (reason: string) => void;
  started: () => void;
  stopped: () => void;
  error: (error: string) => void;
  'permission-denied': () => void;
}

export interface WakeWordSocket {
  connected?: boolean;
  emit(event: string, payload?: unknown): unknown;
}

export class WakeWordService extends EventEmitter<WakeWordEvents> {
  private audioCtx: AudioContext | null = null;
  private micStream: MediaStream | null = null;
  private scriptProcessor: ScriptProcessorNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  /**
   * The socket.io surface this service needs — the connection flag plus `emit`.
   * Structural on purpose: `stores/socket.ts` holds the union of a real Socket.IO
   * client and the WebRTC status bridge, and both answer these two (t_016c7a0e).
   */
  private socket: WakeWordSocket | null = null;
  private _active = false;
  private _paused = false;
  private actualSampleRate = TARGET_SAMPLE_RATE;
  private gateActive = false;
  private gateLastSpeechMs = 0;
  private lastEmitMs = 0;
  private preRollQueue: Int16Array[] = [];
  private captureMode = false;
  private unbindResumeGestures: (() => void) | null = null;
  private sawAudio = false;
  private buffersFlowing = false;
  private audioWatchdog: ReturnType<typeof setTimeout> | null = null;
  private audioWorkletNode: AudioWorkletNode | null = null;
  private workletQueue: Float32Array[] = [];
  private workletQueueLen = 0;
  private noAudioWarned = false;
  private captureBackend: 'worklet' | 'script' | 'mediarecorder' = 'worklet';
  private micRecorder: MediaRecorder | null = null;
  private recorderChunks: Blob[] = [];
  private decodingChunks = false;

  get active(): boolean {
    return this._active;
  }

  get paused(): boolean {
    return this._paused;
  }

  /**
   * Pause audio emission to the backend (keeps mic stream alive).
   * Call when manual voice recording starts to prevent wake word from
   * triggering during the user's command recording.
   */
  pause(): void {
    this._paused = true;
    this.emit('level-change', 0);
  }

  /**
   * Resume audio emission after manual recording ends.
   * Also resumes the AudioContext in case it was suspended by the browser
   * (e.g. after TTS playback or audio session changes).
   */
  async resume(): Promise<void> {
    this._paused = false;
    if (this.audioCtx && this.audioCtx.state === 'suspended') {
      try {
        await this.audioCtx.resume();
        this.unbindResumeOnGesture();
        console.log('[WakeWordService] AudioContext resumed');
      } catch (e) {
        console.warn('[WakeWordService] Failed to resume AudioContext:', e);
      }
    }
  }

  /**
   * Browsers may create the AudioContext in a "suspended" state once the
   * original click's user activation is consumed by the async mic prompt /
   * device enumeration. A suspended context never fires onaudioprocess, so
   * no audio is read from the mic at all (mic indicator on, but silence).
   * Ensure the context is running, falling back to resuming on the next
   * user gesture if the browser blocks an early resume.
   */
  private async ensureAudioRunning(): Promise<void> {
    if (!this.audioCtx) return;
    if (this.audioCtx.state === 'suspended') {
      try {
        await this.audioCtx.resume();
        console.log('[WakeWordService] AudioContext resumed (start)');
      } catch {
        console.warn('[WakeWordService] AudioContext resume blocked — will resume on next user interaction');
      }
    }
    if (this.audioCtx.state === 'suspended') {
      this.bindResumeOnGesture();
    } else {
      this.unbindResumeOnGesture();
    }
  }

  private bindResumeOnGesture(): void {
    if (this.unbindResumeGestures) return;
    const onGesture = (): void => {
      void this.resume();
    };
    window.addEventListener('pointerdown', onGesture, { once: true });
    window.addEventListener('keydown', onGesture, { once: true });
    this.unbindResumeGestures = () => {
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
    };
  }

  private unbindResumeOnGesture(): void {
    if (this.unbindResumeGestures) {
      this.unbindResumeGestures();
      this.unbindResumeGestures = null;
    }
  }

  /**
   * If no non-silent audio arrives shortly after arming, the selected mic is
   * producing silence (wrong device, muted input, driver issue, …). Log it so
   * it can be distinguished from a backend/network detection problem.
   */
  private armAudioWatchdog(delayMs = 5000): void {
    this.clearAudioWatchdog();
    this.audioWatchdog = setTimeout(() => {
      if (!this._active || this.sawAudio) return;

      const ctxState = this.audioCtx?.state ?? 'none';
      if (this.captureBackend !== 'mediarecorder' && ctxState === 'suspended') {
        // Context stopped rendering — try to bring it back and re-check.
        console.warn('[WakeWordService] AudioContext suspended — resuming…');
        void this.resume();
        this.armAudioWatchdog(5000);
        return;
      }

      if (!this.buffersFlowing) {
        // WebAudio can report "running" while its render thread produces
        // nothing (seen in some Edge/Windows setups). MediaRecorder captures
        // straight from the mic without the WebAudio render thread.
        if (this.captureBackend !== 'mediarecorder' && this.micStream) {
          console.warn('[WakeWordService] WebAudio produced no buffers — switching to MediaRecorder fallback…');
          void this.switchToMediaRecorderCapture();
          this.armAudioWatchdog(8000);
          return;
        }
        this.notifyNoAudio(
          'No mic signal is reaching the app (browser audio is not flowing). Try a different mic or restart the browser.',
        );
        return;
      }

      this.notifyNoAudio(
        'Mic is on but silent — check the Windows input level or switch mic (Bluetooth headsets stay silent outside calls).',
      );
    }, delayMs);
  }

  private clearAudioWatchdog(): void {
    if (this.audioWatchdog) {
      clearTimeout(this.audioWatchdog);
      this.audioWatchdog = null;
    }
  }

  /** Log + emit a single, UI-visible warning that the mic produced no audio. */
  private notifyNoAudio(reason: string): void {
    console.warn(`[WakeWordService] ${reason}`);
    if (!this.noAudioWarned) {
      this.noAudioWarned = true;
      this.emit('no-audio', reason);
    }
  }

  /**
   * Try capturing via an AudioWorkletNode (the supported replacement for the
   * deprecated ScriptProcessorNode). Returns false when the browser cannot
   * run the worklet so the caller falls back to ScriptProcessor.
   */
  private async startWorkletCapture(): Promise<boolean> {
    const ctx = this.audioCtx;
    if (!ctx || !this.sourceNode) return false;
    try {
      const blobUrl = URL.createObjectURL(new Blob([WAKE_WORD_WORKLET_SOURCE], { type: 'application/javascript' }));
      try {
        await ctx.audioWorklet.addModule(blobUrl);
      } finally {
        URL.revokeObjectURL(blobUrl);
      }

      const node = new AudioWorkletNode(ctx, 'otto-wake-word-capture', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
        channelCount: 1,
        channelCountMode: 'explicit',
        channelInterpretation: 'speakers',
      });

      node.port.onmessage = (event: MessageEvent) => {
        const data = event.data as Float32Array;
        if (!data?.length) return;
        // Accumulate tiny render-quantum buffers into ~CHUNK_SIZE chunks.
        this.workletQueue.push(data);
        this.workletQueueLen += data.length;
        while (this.workletQueueLen >= CHUNK_SIZE) {
          const out = new Float32Array(CHUNK_SIZE);
          let off = 0;
          while (off < CHUNK_SIZE) {
            const head = this.workletQueue[0];
            const take = Math.min(head.length, CHUNK_SIZE - off);
            out.set(head.subarray(0, take), off);
            off += take;
            if (take === head.length) {
              this.workletQueue.shift();
            } else {
              this.workletQueue[0] = head.subarray(take);
            }
          }
          this.workletQueueLen -= CHUNK_SIZE;
          this.handleSamples(out);
        }
      };
      node.onprocessorerror = () => {
        console.error('[WakeWordService] AudioWorklet processor error — capture stopped');
      };

      // Keep the graph rendering without feeding the mic back to the speakers.
      const silenceGain = ctx.createGain();
      silenceGain.gain.value = 0;
      this.sourceNode.connect(node);
      node.connect(silenceGain);
      silenceGain.connect(ctx.destination);

      this.audioWorkletNode = node;
      console.log('[WakeWordService] Capturing via AudioWorkletNode');
      return true;
    } catch (error) {
      console.warn('[WakeWordService] AudioWorklet not available — falling back to ScriptProcessor:', error);
      return false;
    }
  }

  /** Deprecated ScriptProcessor fallback (widest Chromium/Android support). */
  private startScriptProcessorCapture(): void {
    const ctx = this.audioCtx;
    if (!ctx || !this.sourceNode) return;
    this.scriptProcessor = ctx.createScriptProcessor(CHUNK_SIZE, 1, 1);
    this.scriptProcessor.onaudioprocess = (event) => {
      this.handleSamples(event.inputBuffer.getChannelData(0));
    };
    this.sourceNode.connect(this.scriptProcessor);
    this.scriptProcessor.connect(ctx.destination);
    console.log('[WakeWordService] Capturing via ScriptProcessorNode (fallback)');
  }

  /**
   * Shared per-chunk processing (used by both the AudioWorklet and the
   * ScriptProcessor path): RMS level, diagnostics, resample to 16 kHz,
   * convert to Int16 and gate-emit to the backend.
   */
  private handleSamples(float32: Float32Array): void {
    const now = performance.now();

    // Compute RMS audio level (0–1) for visualization
    let sum = 0;
    for (let i = 0; i < float32.length; i++) {
      sum += float32[i] * float32[i];
    }
    const rms = Math.sqrt(sum / float32.length);
    this.emit('level-change', Math.min(1, rms * 5)); // amplify for visibility

    // Diagnostic: log the first processed buffer so we can tell whether the
    // audio graph is alive (buffers flowing) vs the mic returning silence.
    if (!this.buffersFlowing) {
      this.buffersFlowing = true;
      console.log(`[WakeWordService] Mic buffers flowing (RMS≈${rms.toFixed(4)})`);
    }

    // Diagnostic: log once when real (non-silent) audio starts arriving.
    if (rms > 0.015) {
      if (!this.sawAudio) {
        this.sawAudio = true;
        this.clearAudioWatchdog();
        console.log(`[WakeWordService] Microphone audio detected (RMS≈${rms.toFixed(3)})`);
      }
    }

    // Resample to 16 kHz if needed, then convert to Int16 and send
    const resampled =
      this.actualSampleRate !== TARGET_SAMPLE_RATE
        ? downsample(float32, this.actualSampleRate, TARGET_SAMPLE_RATE)
        : float32;
    const int16 = float32ToInt16(resampled);

    // Hybrid wake-word mode: conservative client-side gating to reduce
    // idle noise upload while preserving enough cadence for server-side
    // openWakeWord detection.
    this.emitChunkWithGate(int16, rms, now);
  }

  /** Bind to a Socket.IO connection. Call before start(). */
  setSocket(socket: WakeWordSocket): void {
    this.socket = socket;
  }

  /** Request mic access and begin streaming PCM16 chunks to the backend. */
  async start(deviceId?: string): Promise<void> {
    if (this._active) return;

    try {
      // When an explicit mic is chosen we disable AEC/NS/AGC: requesting them
      // makes Chromium pick the narrowband 16 kHz "Communications" endpoint of
      // that device, which badly degrades speech. A dedicated wideband mic
      // (esp. wired/USB) captures noticeably better audio for Whisper.
      this.micStream = await navigator.mediaDevices.getUserMedia({
        audio: deviceId
          ? {
              channelCount: 1,
              deviceId: { exact: deviceId },
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false,
            }
          : {
              channelCount: 1,
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
      });
    } catch (error) {
      if (errorName(error) === 'NotAllowedError' || errorName(error) === 'PermissionDeniedError') {
        this.emit('permission-denied');
      } else {
        this.emit('error', errorMessage(error, 'Microphone access failed'));
      }
      return;
    }

    // Request a 16 kHz context. Windows "communications" mic endpoints often
    // run at 16 kHz, and Chromium can silently fail to ingest those into a
    // 48 kHz AudioContext (the touch-panel capture path uses 16 kHz and works).
    // If the browser ignores the hint we still resample in software below.
    this.audioCtx = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
    this.actualSampleRate = this.audioCtx.sampleRate;

    if (this.actualSampleRate !== TARGET_SAMPLE_RATE) {
      console.warn(
        `[WakeWordService] AudioContext running at ${this.actualSampleRate} Hz instead of ${TARGET_SAMPLE_RATE} Hz — will resample`,
      );
    } else {
      console.log(`[WakeWordService] AudioContext running at ${this.actualSampleRate} Hz`);
    }

    // The async getUserMedia / device-id steps above can consume the click's
    // user activation, letting the browser start the context "suspended". A
    // suspended context produces no audio, so resume it before wiring the graph.
    await this.ensureAudioRunning();

    this.sourceNode = this.audioCtx.createMediaStreamSource(this.micStream);
    this.workletQueue = [];
    this.workletQueueLen = 0;

    // AudioWorkletNode is the supported, reliable replacement for the
    // deprecated ScriptProcessorNode; ScriptProcessor stays as a fallback
    // for browsers that cannot run the worklet. If neither produces buffers
    // (WebAudio render thread not running), MediaRecorder is used instead.
    const workletStarted = await this.startWorkletCapture();
    if (workletStarted) {
      this.captureBackend = 'worklet';
    } else {
      this.startScriptProcessorCapture();
      this.captureBackend = 'script';
    }

    this._active = true;
    this.gateActive = false;
    this.gateLastSpeechMs = 0;
    this.lastEmitMs = 0;
    this.preRollQueue = [];
    this.sawAudio = false;
    this.buffersFlowing = false;
    this.captureMode = false;
    this.noAudioWarned = false;
    this.armAudioWatchdog();

    // Tell backend to start wake word detection pipeline
    if (this.socket?.connected) {
      this.socket.emit('agent:wakeword:start');
    }

    this.emit('started');
  }

  /** Remove the WebAudio capture nodes (kept for the MediaRecorder fallback). */
  private teardownWebAudioCaptureNodes(): void {
    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
      this.scriptProcessor.onaudioprocess = null;
      this.scriptProcessor = null;
    }
    if (this.audioWorkletNode) {
      try {
        this.audioWorkletNode.port.onmessage = null;
        this.audioWorkletNode.disconnect();
      } catch {
        /* ignore */
      }
      this.audioWorkletNode = null;
    }
    this.workletQueue = [];
    this.workletQueueLen = 0;
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }
    if (this.audioCtx) {
      this.audioCtx.close().catch(() => undefined);
      this.audioCtx = null;
    }
  }

  /**
   * MediaRecorder fallback for environments where the WebAudio render thread
   * never produces buffers (context reports "running" but no callbacks fire).
   * MediaRecorder captures straight from the mic without that render thread;
   * chunks are decoded with OfflineAudioContext (decode does not need an
   * actively rendering context) and fed into the shared pipeline.
   */
  private async switchToMediaRecorderCapture(): Promise<void> {
    if (!this.micStream || this.captureBackend === 'mediarecorder') return;
    this.teardownWebAudioCaptureNodes();
    this.captureBackend = 'mediarecorder';

    const mimeType = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus'].find(
      (t) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t),
    );
    // Higher bitrate for the command/capture path -> cleaner STT input.
    const recorderOptions: MediaRecorderOptions = {
      audioBitsPerSecond: 128_000,
      ...(mimeType ? { mimeType } : {}),
    };
    try {
      this.micRecorder = new MediaRecorder(this.micStream, recorderOptions);
    } catch {
      try {
        this.micRecorder = new MediaRecorder(this.micStream);
      } catch {
        this.notifyNoAudio('MediaRecorder capture could not start.');
        return;
      }
    }

    this.recorderChunks = [];
    this.micRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        this.recorderChunks.push(event.data);
        void this.pumpDecode();
      }
    };
    this.micRecorder.onerror = () => {
      this.notifyNoAudio('MediaRecorder capture failed.');
    };
    this.micRecorder.start(250);
    console.log(`[WakeWordService] Capturing via MediaRecorder (${this.micRecorder.mimeType})`);
  }

  private async pumpDecode(): Promise<void> {
    if (this.decodingChunks || this.recorderChunks.length === 0) return;
    const blob = new Blob(this.recorderChunks, {
      type: this.micRecorder?.mimeType || 'audio/webm',
    });
    this.recorderChunks = [];
    this.decodingChunks = true;
    try {
      await this.decodeBlob(blob);
    } finally {
      this.decodingChunks = false;
      if (this.recorderChunks.length > 0) void this.pumpDecode();
    }
  }

  private async decodeBlob(blob: Blob): Promise<void> {
    try {
      const buf = await blob.arrayBuffer();
      const decoderCtx = new OfflineAudioContext(1, 1, TARGET_SAMPLE_RATE);
      const audioBuffer = await decoderCtx.decodeAudioData(buf);
      const channel = audioBuffer.getChannelData(0);
      const srcRate = audioBuffer.sampleRate;
      const f32 =
        srcRate !== TARGET_SAMPLE_RATE ? downsample(channel as Float32Array, srcRate, TARGET_SAMPLE_RATE) : channel;
      this.handleSamples(f32);
    } catch (e) {
      console.warn('[WakeWordService] MediaRecorder chunk decode failed:', e);
    }
  }

  /** Stop capturing and release all resources. */
  async stop(): Promise<void> {
    if (!this._active) return;
    this._active = false;
    this.unbindResumeOnGesture();
    this.clearAudioWatchdog();
    this.buffersFlowing = false;
    this.sawAudio = false;
    this.captureMode = false;

    if (this.micRecorder) {
      try {
        if (this.micRecorder.state !== 'inactive') this.micRecorder.stop();
      } catch {
        /* ignore */
      }
      this.micRecorder = null;
    }
    this.recorderChunks = [];
    this.captureBackend = 'worklet';

    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
      this.scriptProcessor.onaudioprocess = null;
      this.scriptProcessor = null;
    }
    if (this.audioWorkletNode) {
      try {
        this.audioWorkletNode.port.onmessage = null;
        this.audioWorkletNode.disconnect();
      } catch {
        /* ignore */
      }
      this.audioWorkletNode = null;
    }
    this.workletQueue = [];
    this.workletQueueLen = 0;
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }
    if (this.audioCtx) {
      await this.audioCtx.close().catch(() => undefined);
      this.audioCtx = null;
    }
    if (this.micStream) {
      this.micStream.getTracks().forEach((t) => t.stop());
      this.micStream = null;
    }

    if (this.socket?.connected) {
      this.socket.emit('agent:wakeword:stop');
    }

    this.emit('level-change', 0);
    this.emit('stopped');
  }

  /** Signal to the backend that the user finished speaking their command. */
  commitCapture(): void {
    if (this.socket?.connected) {
      this.socket.emit('agent:wakeword:commit');
    }
  }

  /**
   * Switch to lossless streaming once the wake word is detected: the speech
   * gate is disabled so the whole command is captured without clipped starts.
   */
  beginCommandCapture(): void {
    if (this.captureMode) return;
    this.captureMode = true;
    console.log('[WakeWordService] Command capture mode ON (gating off)');
    // Flush any buffered pre-roll immediately so no command start is lost.
    if (this.preRollQueue.length > 0) {
      const flush = this.preRollQueue;
      this.preRollQueue = [];
      for (const chunk of flush) this.emitChunk(chunk);
    }
  }

  /** Return to gated detection streaming after the command finished. */
  endCommandCapture(): void {
    this.captureMode = false;
    this.preRollQueue = [];
  }

  // ── Helpers ────────────────────────────────────────────────────────────

  private emitChunk(samples: Int16Array): void {
    if (!this.socket?.connected) return;
    this.socket.emit('agent:wakeword:audio', samples.buffer);
  }

  private emitChunkWithGate(samples: Int16Array, rms: number, nowMs: number): void {
    // Don't send audio to backend when paused (e.g. during manual recording)
    if (this._paused) return;

    // Full-fidelity command capture: once the wake word was detected, send
    // every sample without gating so the command is captured completely.
    if (this.captureMode) {
      this.preRollQueue.length = 0;
      this.emitChunk(samples);
      return;
    }

    // Keep a short rolling pre-roll so the start of speech is not clipped.
    this.preRollQueue.push(samples);
    if (this.preRollQueue.length > PRE_ROLL_CHUNKS) {
      this.preRollQueue.shift();
    }

    const aboveOpen = rms >= GATE_OPEN_RMS;
    const aboveClose = rms >= GATE_CLOSE_RMS;

    if (aboveOpen) {
      this.gateActive = true;
      this.gateLastSpeechMs = nowMs;
    } else if (this.gateActive && (aboveClose || nowMs - this.gateLastSpeechMs <= GATE_HANGOVER_MS)) {
      if (aboveClose) this.gateLastSpeechMs = nowMs;
    } else {
      this.gateActive = false;
    }

    if (this.gateActive) {
      const flush = this.preRollQueue;
      this.preRollQueue = [];
      for (const chunk of flush) {
        this.emitChunk(chunk);
      }
      this.lastEmitMs = nowMs;
      return;
    }

    // Keep periodic silence cadence so the server detector stays primed.
    if (nowMs - this.lastEmitMs >= SILENT_KEEPALIVE_MS) {
      this.emitChunk(samples);
      this.lastEmitMs = nowMs;
    }
  }
}

function float32ToInt16(input: Float32Array): Int16Array {
  const output = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return output;
}

/** Linear-interpolation downsampler (e.g. 48 kHz → 16 kHz). */
function downsample(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) return input;
  const ratio = fromRate / toRate;
  const outputLen = Math.round(input.length / ratio);
  const output = new Float32Array(outputLen);
  for (let i = 0; i < outputLen; i++) {
    const srcIdx = i * ratio;
    const lower = Math.floor(srcIdx);
    const upper = Math.min(lower + 1, input.length - 1);
    const frac = srcIdx - lower;
    output[i] = input[lower] * (1 - frac) + input[upper] * frac;
  }
  return output;
}

/** Singleton instance */
export const wakeWordService = new WakeWordService();
